import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardSubtitle, CardTitle, Wordmark } from '@keobook/ui';
import { useAuth } from '../lib/auth';
import { ApiError, api } from '../lib/api';

const PENDING_KEY = 'kb_pending_invite';

type Invite = { email: string; role: string; businessName: string };

/**
 * Collaborator invitation acceptance (/invite?token=…). The invite link is the
 * credential — it was emailed to the invited address. Flow, one round-trip:
 *   - Not signed in → "Create account" (email pre-filled from the invite) or
 *     "Sign in". After auth, GuidedRoute routes back here via the stashed token.
 *   - Signed in as the invited email → we accept automatically (no separate
 *     code) and drop the invitee on the business dashboard.
 */
export function InviteAccept() {
  const { status, user, refresh } = useAuth();
  const navigate = useNavigate();
  const [token, setToken] = useState<string | null>(null);
  const [invite, setInvite] = useState<Invite | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const startedRef = useRef(false);

  // Resolve the token (from the URL, or a prior session) + load invite context.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const t = params.get('token') ?? localStorage.getItem(PENDING_KEY);
    if (params.has('token')) window.history.replaceState({}, '', '/invite');
    if (!t) return;
    setToken(t);
    try {
      localStorage.setItem(PENDING_KEY, t);
    } catch {
      /* ignore */
    }
    api
      .getInvitation(t)
      .then(setInvite)
      .catch(() => setLoadError('This invitation is invalid or has expired.'));
  }, []);

  // Auto-accept once we have a token AND an authenticated session. Runs once.
  useEffect(() => {
    if (!token || status !== 'authenticated' || startedRef.current) return;
    startedRef.current = true;
    api
      .acceptInvitation(token)
      .then(async () => {
        try {
          localStorage.removeItem(PENDING_KEY);
        } catch {
          /* ignore */
        }
        await refresh().catch(() => undefined); // pick up the new client link
        navigate('/dashboard', { replace: true });
      })
      .catch((err) => {
        setError(err instanceof ApiError ? err.message : 'Could not accept the invitation.');
      });
  }, [token, status, navigate, refresh]);

  const roleLabel = invite?.role === 'bookkeeper' ? 'bookkeeper' : 'accountant';
  const signupHref = invite ? `/signup?email=${encodeURIComponent(invite.email)}` : '/signup';
  const wrongAccount =
    !!invite && !!user && user.email.toLowerCase() !== invite.email.toLowerCase();

  return (
    <main className="kb-auth-page">
      <div className="kb-auth-container">
        <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
          <Wordmark size="lg" tagline="You’ve been invited to collaborate" />
        </div>
        <Card size="xl">
          {!token ? (
            <>
              <CardTitle>Invitation link is missing</CardTitle>
              <CardSubtitle>Open the link from your invitation email to continue.</CardSubtitle>
            </>
          ) : loadError ? (
            <>
              <CardTitle>Invitation unavailable</CardTitle>
              <CardSubtitle>{loadError} Ask whoever invited you to send a new one.</CardSubtitle>
            </>
          ) : status !== 'authenticated' ? (
            <>
              <CardTitle>
                {invite ? `Join ${invite.businessName} on KEOBOOKS` : 'Accept your invitation'}
              </CardTitle>
              <CardSubtitle>
                {invite ? (
                  <>
                    You’ve been invited to collaborate as {invite.businessName}’s {roleLabel} (
                    <strong>{invite.email}</strong>). Create your account with that email to join —
                    no confirmation code needed.
                  </>
                ) : (
                  <>Create an account, or sign in with the invited email, to join.</>
                )}
              </CardSubtitle>
              <div style={{ display: 'flex', gap: 8, marginTop: '1rem' }}>
                <Link to={signupHref}>
                  <Button>Create account</Button>
                </Link>
                <Link to="/signin">
                  <Button variant="ghost">I already have an account</Button>
                </Link>
              </div>
            </>
          ) : error ? (
            <>
              <CardTitle>Couldn’t accept this invitation</CardTitle>
              <CardSubtitle>
                {error}
                {wrongAccount ? (
                  <>
                    {' '}
                    You’re signed in as <strong>{user!.email}</strong>, but this invite is for{' '}
                    <strong>{invite!.email}</strong>. Sign out and sign in as that address to
                    accept.
                  </>
                ) : null}
              </CardSubtitle>
              <div style={{ marginTop: '1rem' }}>
                <Link to="/dashboard">
                  <Button variant="ghost">Back to dashboard</Button>
                </Link>
              </div>
            </>
          ) : (
            <>
              <CardTitle>Joining{invite ? ` ${invite.businessName}` : ''}…</CardTitle>
              <CardSubtitle>One moment while we add you to the books.</CardSubtitle>
            </>
          )}
        </Card>
      </div>
    </main>
  );
}
